/**
 * ComfortImpactCard Component
 *
 * Leads with EMOTION - the customer's reported comfort issues.
 * Quantifies each issue's cost impact to make pain tangible.
 * "We sell benefits, not features" - start with how they FEEL.
 */

import { calculateComfortIssueCosts } from '../../../utils/calculatorFunctions';

// Comfort issue definitions with emotional messaging
const COMFORT_ISSUES = {
  hotCold: {
    icon: '🌡️',
    label: 'Hot/Cold Spots',
    description: 'Rooms that won\'t heat or cool properly',
    emotionalImpact: 'Uncomfortable living spaces affect your daily comfort and peace of mind'
  },
  humidity: {
    icon: '💧',
    label: 'Humidity Issues',
    description: 'Air feels too dry in winter or sticky in summer',
    emotionalImpact: 'Poor humidity control impacts health, skin comfort, and sleep quality'
  },
  runsConstantly: {
    icon: '⏱️',
    label: 'Runs Constantly',
    description: 'System never seems to shut off or cycles frequently',
    emotionalImpact: 'Constant running means your system is struggling and wasting energy'
  },
  highBills: {
    icon: '⚡',
    label: 'High Energy Bills',
    description: 'Bills higher than expected for your home size',
    emotionalImpact: 'Every month you\'re paying more than you should for comfort'
  },
  loud: {
    icon: '🔊',
    label: 'Excessive Noise',
    description: 'Rattling, banging, or unusual sounds during operation',
    emotionalImpact: 'Disruptive noise affects relaxation and indicates potential failure'
  },
  tempTrouble: {
    icon: '🎯',
    label: 'Can\'t Maintain Temperature',
    description: 'Struggles to reach thermostat setting',
    emotionalImpact: 'Never feeling truly comfortable in your own home'
  }
};

export default function ComfortImpactCard({ issues, annualEnergyCost, squareFootage }) {
  // Count selected issues
  const selectedIssues = Object.keys(issues).filter(key => issues[key]);

  // If no issues selected, don't show this card
  if (selectedIssues.length === 0) {
    return null;
  }

  // Calculate cost impact of each issue
  const comfortCosts = calculateComfortIssueCosts(issues, annualEnergyCost, squareFootage);

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '25px'
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '3px solid #ef4444',
        paddingBottom: '15px',
        marginBottom: '20px'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '700',
          color: '#ef4444',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '8px'
        }}>
          Your Current Comfort Issues
        </div>
        <h3 style={{
          margin: 0,
          fontSize: '24px',
          color: '#1e293b',
          fontWeight: '700'
        }}>
          The Cost of Discomfort
        </h3>
        <p style={{
          margin: '8px 0 0 0',
          fontSize: '15px',
          color: '#64748b',
          lineHeight: '1.5'
        }}>
          You reported {selectedIssues.length} comfort {selectedIssues.length === 1 ? 'issue' : 'issues'} with your current system.
          Here's what {selectedIssues.length === 1 ? 'it\'s' : 'they\'re'} really costing you:
        </p>
      </div>

      {/* Issues List */}
      <div style={{ marginBottom: '25px' }}>
        {selectedIssues.map((issueKey) => {
          const issue = COMFORT_ISSUES[issueKey];
          const cost = comfortCosts[issueKey];

          return (
            <div
              key={issueKey}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '18px',
                marginBottom: '12px',
                background: '#fef2f2',
                border: '2px solid #fecaca',
                borderRadius: '10px'
              }}
            >
              {/* Icon */}
              <div style={{
                fontSize: '32px',
                marginRight: '15px',
                flexShrink: 0
              }}>
                {issue.icon}
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: '700',
                  fontSize: '16px',
                  color: '#1e293b',
                  marginBottom: '4px'
                }}>
                  {issue.label}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#64748b',
                  marginBottom: '8px'
                }}>
                  {issue.description}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#dc2626',
                  fontStyle: 'italic',
                  lineHeight: '1.4'
                }}>
                  💔 {issue.emotionalImpact}
                </div>
              </div>

              {/* Cost Impact */}
              <div style={{
                textAlign: 'right',
                marginLeft: '20px',
                flexShrink: 0
              }}>
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#dc2626'
                }}>
                  ${cost.toLocaleString()}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginTop: '2px'
                }}>
                  per year
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total Impact */}
      <div style={{
        padding: '20px',
        background: '#fee2e2',
        borderRadius: '10px',
        border: '2px solid #ef4444'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#991b1b',
              marginBottom: '4px'
            }}>
              Total Annual Cost of These Issues
            </div>
            <div style={{
              fontSize: '13px',
              color: '#64748b'
            }}>
              This is money you're losing every year while living uncomfortably
            </div>
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: '800',
            color: '#dc2626'
          }}>
            ${comfortCosts.total.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Emotional Call-Out */}
      <div style={{
        marginTop: '20px',
        padding: '15px 20px',
        background: '#fffbeb',
        borderLeft: '4px solid #f59e0b',
        borderRadius: '6px'
      }}>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: '#92400e',
          lineHeight: '1.6'
        }}>
          <strong>💡 Bottom Line:</strong> Every day you delay addressing these issues,
          you're paying the price in both comfort and money. A new, properly sized system
          eliminates these problems while significantly reducing your energy costs.
        </p>
      </div>
    </div>
  );
}
